<template>
  <div class="template-info w-full mx-auto">
    <div class="title flex justify-between items-center">
      {{ templateData!.name }}
      <AiMark v-if="templateData?.show_ai_icon" class="ai-mark" theme="mobile" />
    </div>
    <div class="info">
      <div class="author">{{ $t('zuo-zhe') }}{{ $t('colon') }}{{ displayAuthor }}</div>
      <div class="divider"></div>
      <div class="release-time">
        {{ $t('fa-bu-yu') }}
        {{ displayOnlineTime }}
      </div>
    </div>
    <div class="description" v-html="displayDescription"></div>
    <div class="video">
      <XVideo controls :poster="displayCover" :muted="true" :autoplay="true">
        <source :src="displayVideo" />
      </XVideo>
    </div>

    <SimilarM type="template" :meta="templateData!" />

    <div class="operations">
      <x-button v-if="isChinese()" theme="blank-primary" @click="() => showContact()">
        {{ $t('lian-xi-wo-men') }}
      </x-button>
      <x-button theme="blank-default" @click="handleCopyClick">
        <iconpark-icon name="share-link" class="icon-link" />
        {{ $t('fu-zhi-lian-jie-fen-xiang-gai-nei-rong') }}
      </x-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { isEmpty } from 'lodash-es';
import message from '~/components-mobile/atom/x-alert/index.ts';
import XButton from '~/components-mobile/atom/x-button.vue';
import XVideo from '~/components-mobile/atom/x-video.vue';
import SimilarM from '~/components-mobile/similar/index.vue';
import { showContact } from '~/hooks/useModal/modals/contact';
import { isChinese } from '~/utils/area';

import { getTemplate, getTemplateShareText } from '~/api';
import type { Template } from '~/api/interface';
import AiMark from '~/components/ai-mark/index.vue';
import { useWatchPageSeoData } from '~/hooks/useSeo';
import { useTime } from '~/hooks/useTime';
import { copy } from '~/utils/copy.ts';

const { timeFormat } = useTime();

const route = useRoute();

const templateId = computed(() => Number(String(route.params.id).split('-')[0]));

const { $t } = useNuxtApp();

function autoSuffix(t: string) {
  const suffix = isChinese() ? `-${$t('you-yan')}AI` : ` | ${$t('you-yan')}`;
  if (t.endsWith(suffix)) {
    return t;
  }

  return t + suffix;
}

function getSeoDescriptionFromScript(text: string) {
  const str = text.split('\n')[0] || '';
  return str.slice(0, isChinese() ? 30 : 60);
}

const { data: templateData } = useAsyncData(async () => {
  const { data } = await getTemplate({
    template_id: templateId.value,
    query_star: import.meta.client,
  });
  if (!isEmpty(data)) {
    data.seo_title = data.seo_title || autoSuffix(data.name);
    data.seo_description =
      data.seo_description || data.message || getSeoDescriptionFromScript(data.segment_text);
    data.query_star = import.meta.client;
  }
  return data as Template;
});

watch(
  () => templateData.value,
  data => {
    if (data && !data?.id) {
      throw new Error(`no template found for id: ${templateId.value}`);
    }
  },
  { immediate: true, flush: 'sync' },
);

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: templateData.value?.seo_title,
  description: templateData.value?.seo_description,
  contentUrl: templateData.value?.preview_m3u8_url || templateData.value?.full_video_url,
  uploadDate: templateData.value?.create_time,
  thumbnailUrl: templateData.value?.cover_url,
}));

const displayCover = computed(() => {
  if (!templateData.value) {
    return '';
  }

  return transformImage(templateData.value.cover_url);
});

const displayAuthor = computed(() => {
  if (!templateData.value) {
    return '';
  }

  return (
    templateData.value?.name_for_show ||
    templateData.value?.homepage_org_name ||
    templateData.value?.username
  );
});

const displayOnlineTime = computed(() => {
  if (!templateData.value) {
    return '';
  }

  return timeFormat(templateData.value?.online_time || templateData.value?.create_time);
});

const displayDescription = computed(() => {
  if (!templateData.value) {
    return '';
  }

  return templateData.value?.message;
});

const displayVideo = computed(() => {
  if (!templateData.value) {
    return '';
  }

  return templateData.value.preview_m3u8_url || templateData.value?.full_video_url;
});

useWatchPageSeoData(templateData);

const handleCopyClick = async () => {
  const res = await getTemplateShareText({
    template_id: templateId.value,
  });
  if (res?.data?.length) {
    const link = window.location.href;
    const shareText = `${res.data?.[0] || ''} ${link} ${res.data?.[1] || ''}`;
    copy(shareText);
  }
  message.success($t('fu-zhi-cheng-gong'));
};
</script>

<style scoped lang="scss">
$c1: rgba(18, 25, 38, 1);
$c2: rgba(18, 25, 38, 0.4);
$c3: rgba(18, 25, 38, 0.6);
$c4: rgba(18, 25, 38, 0.8);
$primary-color: var(--purple-6);

.operations {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: m-rem(20) m-rem(16);
}

.icon-link {
  margin-right: m-rem(6);
}

.description {
  color: $c3;
  margin-bottom: m-rem(12);
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
}

.template-info {
  padding: m-rem(20) m-rem(16) m-rem(130);

  .title {
    font-weight: var(--x-weight-medium, 500);
    font-size: m-rem(18);
    color: $c1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    line-height: m-rem(24);
  }

  .info {
    font-size: m-rem(12);
    color: $c2;
    line-height: m-rem(18);
    margin-top: m-rem(7);
    margin-bottom: m-rem(15);
    display: flex;
    align-items: center;

    .divider {
      margin: 0 m-rem(8);
      width: 1px;
      height: 11px;
      background: $c2;
    }
  }
}
</style>
